import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { translateBlock } from 'i18n';
import { useSelector } from 'store/hooks';
import { useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import StyPageContainer from 'ui/PageContainer/PageContainer';
import HeaderLayout from 'ui/HeaderLayout/HeaderLayout';
import { useGetAutoGeneratedTables } from 'routes/ImportCsv/ImportWorkflow/hooks/tableMetadata';
import { useGetAvailableMetrics } from 'routes/Explore/components/Sentence/hooks/available';
import SideList from './components/SideList/SideList';
import {
    getFileImportTablesV1,
    FILES_IMPORT,
    groupTableByFunction,
} from './helpers';
import './components/_cellar.scss';
import {
    useShouldShowFilesImport,
    useShouldShowFilesImportV2,
} from './hooks/csvAccess';
import CellarContent from './components/CellarContent';
import NewTableContextProvider from './context/NewTable/NewTableProvider';
import { TableT } from './components/DomainDetails';

const StyCellarPageContainer = styled(StyPageContainer, {
    name: 'StyCellarPageContainer',
})({
    padding: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
});

const Cellar = (): React.ReactElement | null => {
    const { t } = useTranslation();
    const client = useSelector((state) => state.vision.client);
    const isSupportUser = useSelector((state) => state.vision.isSupportUser);

    const shouldShowFilesImport = useShouldShowFilesImport();
    const shouldShowFilesImportV2 = useShouldShowFilesImportV2();

    const { func: selectedFunction = '' } = useParams();

    const { data: availableMetrics, isLoading } = useGetAvailableMetrics({});

    const {
        data: autoGeneratedTables,
        isLoading: isAutoGeneratedTableLoading,
    } = useGetAutoGeneratedTables({
        initialData: [],
        enabled: shouldShowFilesImportV2 || isSupportUser,
    });

    const tablesByFunction = useMemo(
        () => groupTableByFunction(availableMetrics, autoGeneratedTables),
        [JSON.stringify(availableMetrics), JSON.stringify(autoGeneratedTables)],
    );

    const tablesDict = useMemo(() => {
        const tableByDomain: {
            [func: string]: TableT[];
        } = { global: [] };
        Object.keys(tablesByFunction).forEach((f) => {
            tableByDomain[f] = tablesByFunction[f].map((table) => ({
                label: translateBlock(table),
                value: table,
                shouldShowTable: true,
            }));
        });

        if (shouldShowFilesImport || isSupportUser) {
            const fileImportTablesV1 = getFileImportTablesV1(client);
            tableByDomain.filesImport = fileImportTablesV1.map((tableName) => ({
                label: translateBlock(tableName),
                value: tableName,
                rights: ['read', 'write'], // Everyone can read and write on the V1 tables
                shouldShowTable: true,
            }));
            tableByDomain.filesImport.push(
                ...autoGeneratedTables!
                    .filter((table) => !fileImportTablesV1.includes(table.name))
                    .map((table) => ({
                        label: table.label,
                        value: table.name,
                        rights: table.rights,
                        isArchived: table.isArchived,
                        shouldShowTable: !table.isArchived || isSupportUser,
                    })),
            );
        }
        return tableByDomain;
    }, [
        JSON.stringify(tablesByFunction),
        JSON.stringify(autoGeneratedTables),
        shouldShowFilesImport,
        isSupportUser,
    ]);

    const functionsList = Object.keys(tablesDict)
        .map((value) => ({
            label: t(`cellar.${value}`),
            value: value.toString(),
        }))
        .sort((a, b) => (a.label < b.label ? -1 : 1));

    const sideListItems = useMemo(() => {
        const list = [
            {
                name: t('cellar.domains'),
                items: functionsList.filter(
                    (item) => item.value !== FILES_IMPORT,
                ),
            },
        ];

        if (functionsList.some((item) => item.value === FILES_IMPORT)) {
            list.push({
                name: t('cellar.dataImport'),
                items: functionsList.filter(
                    (item) => item.value === FILES_IMPORT,
                ),
            });
        }

        return list;
    }, [functionsList]);

    return (
        <StyCellarPageContainer>
            <HeaderLayout title={t('cellar.title')} />

            <Box className="cellar-page-container">
                <Box className="cellar-container">
                    <SideList
                        loading={isLoading || isAutoGeneratedTableLoading}
                        items={sideListItems}
                        selectedItemKey={selectedFunction}
                    />
                    <NewTableContextProvider>
                        <CellarContent
                            isLoading={isLoading}
                            tables={tablesDict[selectedFunction] ?? []}
                        />
                    </NewTableContextProvider>
                </Box>
            </Box>
        </StyCellarPageContainer>
    );
};

export default Cellar;
